import React, { useState, useContext } from 'react';
import { observer } from 'mobx-react';
import { format, formatDistanceToNow } from 'date-fns';
import Card from 'src/components/Card';
import Flex from 'src/components/Flex';
import Table from 'src/components/Table';
import Button from 'src/components/Button';
import CenterModal from 'src/components/CenterModal';
import ModalHeader from 'src/components/ModalHeader';
import ModalFooter from 'src/components/ModalFooter';
import Input from 'src/components/Input';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import UserStoreContext from 'src/stores/UserStore';
import { ToastStoreObject, ToastType } from 'src/stores/ToastStore';
import { parseError } from 'src/utils';
import { DISPLAY_DATE_FORMAT } from 'src/utils/constants';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from 'src/queries/useApiKeys';
import { CreateApiKeyData, CreateApiKeyResponse } from 'src/api/apiKeys';
import { CellInfo } from 'react-table';
import styles from './index.module.scss';

const ALL_SCOPES = [
    'health',
    'changes:read',
    'encounters:read',
    'encounters:write',
    'encounter-forms:read',
    'encounter-forms:write',
    'providers:read',
    'providers:write',
    'reference-lists:read',
    'reference-lists:write',
    'value-sets:read',
    'value-sets:write',
    'facilities:read',
    'facilities:write',
];

const ApiKeys = observer(function ApiKeys() {
    const userStore = useContext(UserStoreContext);
    const { data: apiKeys, isLoading } = useApiKeys();
    const createApiKey = useCreateApiKey();
    const revokeApiKey = useRevokeApiKey();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRevealModal, setShowRevealModal] = useState(false);
    const [showRevokeConfirm, setShowRevokeConfirm] = useState<number | null>(null);
    const [newKeyResponse, setNewKeyResponse] = useState<CreateApiKeyResponse | null>(null);
    const [copied, setCopied] = useState(false);

    const [createForm, setCreateForm] = useState<CreateApiKeyData>({
        label: '',
        scopes: [...ALL_SCOPES],
    });

    const columns = [
        {
            Header: 'PREFIX',
            accessor: 'prefix',
            minWidth: 80,
            Cell: (props: CellInfo) => <code>{props.value}</code>,
        },
        {
            Header: 'LABEL',
            accessor: 'label',
            minWidth: 160,
        },
        {
            Header: 'SCOPES',
            accessor: 'scopes',
            minWidth: 100,
            Cell: (props: CellInfo) => {
                const scopes = props.value as string[];
                if (scopes.length === ALL_SCOPES.length) return <span>All</span>;
                return <span>{scopes.length} scope{scopes.length !== 1 ? 's' : ''}</span>;
            },
        },
        {
            Header: 'FACILITIES',
            accessor: 'facilityIds',
            minWidth: 100,
            Cell: (props: CellInfo) => {
                const ids = props.value as number[] | null;
                return <span>{ids ? `${ids.length} selected` : 'All'}</span>;
            },
        },
        {
            Header: 'LAST USED',
            accessor: 'lastUsedAt',
            minWidth: 120,
            Cell: (props: CellInfo) =>
                props.value ? (
                    <span>{formatDistanceToNow(new Date(props.value), { addSuffix: true })}</span>
                ) : (
                    <span className={styles.muted}>Never</span>
                ),
        },
        {
            Header: 'CREATED',
            accessor: 'createdAt',
            minWidth: 100,
            Cell: (props: CellInfo) => <span>{format(new Date(props.value), DISPLAY_DATE_FORMAT)}</span>,
        },
        {
            Header: 'ACTIONS',
            minWidth: 100,
            Cell: (props: CellInfo) => (
                <Button
                    type="secondary"
                    text="Revoke"
                    isLocked={!userStore.userPermissions.canCreate.apiKeys}
                    onClick={() => setShowRevokeConfirm(props.original.apiKeyId)}
                />
            ),
        },
    ];

    async function handleCreate() {
        if (!createForm.label.trim()) {
            ToastStoreObject.show('Label is required', ToastType.Warn);
            return;
        }
        if (createForm.scopes.length === 0) {
            ToastStoreObject.show('At least one scope is required', ToastType.Warn);
            return;
        }

        try {
            const response = await createApiKey.mutateAsync(createForm);
            setNewKeyResponse(response);
            setShowCreateModal(false);
            setShowRevealModal(true);
            setCreateForm({ label: '', scopes: [...ALL_SCOPES] });
            ToastStoreObject.show('API key created', ToastType.Success);
        } catch (e) {
            ToastStoreObject.show(parseError(e));
        }
    }

    async function handleRevoke(apiKeyId: number) {
        try {
            await revokeApiKey.mutateAsync(apiKeyId);
            setShowRevokeConfirm(null);
            ToastStoreObject.show('API key revoked', ToastType.Success);
        } catch (e) {
            ToastStoreObject.show(parseError(e));
        }
    }

    function toggleScope(scope: string) {
        setCreateForm((prev) => ({
            ...prev,
            scopes: prev.scopes.includes(scope) ? prev.scopes.filter((s) => s !== scope) : [...prev.scopes, scope],
        }));
    }

    function toggleAllScopes() {
        setCreateForm((prev) => ({
            ...prev,
            scopes: prev.scopes.length === ALL_SCOPES.length ? [] : [...ALL_SCOPES],
        }));
    }

    async function copyToClipboard() {
        if (newKeyResponse?.plaintext) {
            await navigator.clipboard.writeText(newKeyResponse.plaintext);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    }

    return (
        <PageContainer>
            <PageContent>
                <Flex direction="row" align="center" justify="between" className={styles.header}>
                    <h2>API Keys</h2>
                    <Button
                        type="primary"
                        text="Create API Key"
                        isLocked={!userStore.userPermissions.canCreate.apiKeys}
                        onClick={() => setShowCreateModal(true)}
                    />
                </Flex>

                <Card>
                    <Table
                        data={apiKeys || []}
                        columns={columns}
                        loading={isLoading}
                        defaultPageSize={20}
                        noDataText="No API keys found"
                    />
                </Card>

                {/* Create Modal */}
                <CenterModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
                    <ModalHeader title="Create API Key" onClose={() => setShowCreateModal(false)} />
                    <div className={styles.modalBody}>
                        <div className={styles.field}>
                            <label>Label</label>
                            <Input
                                value={createForm.label}
                                onChange={(e: any) =>
                                    setCreateForm((prev) => ({ ...prev, label: e.target.value }))
                                }
                                placeholder="e.g., Graphium AI Production"
                            />
                        </div>
                        <div className={styles.field}>
                            <Flex direction="row" align="center" justify="between">
                                <label>Scopes</label>
                                <Button
                                    type="small"
                                    text={createForm.scopes.length === ALL_SCOPES.length ? 'Deselect All' : 'Select All'}
                                    onClick={toggleAllScopes}
                                />
                            </Flex>
                            <div className={styles.scopeGrid}>
                                {ALL_SCOPES.map((scope) => (
                                    <label key={scope} className={styles.scopeCheckbox}>
                                        <input
                                            type="checkbox"
                                            checked={createForm.scopes.includes(scope)}
                                            onChange={() => toggleScope(scope)}
                                        />
                                        <span>{scope}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <ModalFooter
                        primaryText="Create"
                        primaryClick={handleCreate}
                        secondaryText="Cancel"
                        secondaryClick={() => setShowCreateModal(false)}
                    />
                </CenterModal>

                {/* Key Reveal Modal */}
                <CenterModal isOpen={showRevealModal && !!newKeyResponse} onClose={() => setShowRevealModal(false)}>
                    <ModalHeader title="API Key Created" onClose={() => setShowRevealModal(false)} />
                    <div className={styles.modalBody}>
                        <div className={styles.warning}>
                            This key will only be shown once. Copy it now.
                        </div>
                        <div className={styles.keyDisplay}>
                            <code className={styles.keyText}>{newKeyResponse?.plaintext}</code>
                            <Button
                                type="secondary"
                                text={copied ? 'Copied!' : 'Copy'}
                                onClick={copyToClipboard}
                            />
                        </div>
                    </div>
                    <ModalFooter
                        primaryText="Done"
                        primaryClick={() => {
                            setShowRevealModal(false);
                            setNewKeyResponse(null);
                            setCopied(false);
                        }}
                    />
                </CenterModal>

                {/* Revoke Confirmation Modal */}
                <CenterModal isOpen={showRevokeConfirm !== null} onClose={() => setShowRevokeConfirm(null)}>
                    <ModalHeader title="Revoke API Key" onClose={() => setShowRevokeConfirm(null)} />
                    <div className={styles.modalBody}>
                        <p>
                            Are you sure you want to revoke this API key? Any integrations using this key will
                            immediately lose access.
                        </p>
                    </div>
                    <ModalFooter
                        primaryText="Revoke"
                        primaryClick={() => showRevokeConfirm !== null && handleRevoke(showRevokeConfirm)}
                        secondaryText="Cancel"
                        secondaryClick={() => setShowRevokeConfirm(null)}
                    />
                </CenterModal>
            </PageContent>
        </PageContainer>
    );
});

export default ApiKeys;
